var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { fmtDate } from '../format';
import { LocaleController } from '../i18n/LocaleController';
import { chip } from '../components/templates';
import './vitesse-icon';
import './inputs/vitesse-select';
import { memberAvatar } from './member-avatar';
/*
 * THE MEMBERS SCREEN, OURS, ON THE PROVIDER'S DATA.
 *
 * The provider ships a Members page and it is genuinely good at the parts that
 * are ITS job: who is in the org, who has been invited, what happens when you
 * revoke. It is not good at looking like the rest of this product, and theming it
 * from the outside only ever got close — a table we do not own, rows sized by
 * rules we do not set, and a layout that shifts as its own async loads land.
 *
 * So this is the clean merge rather than either extreme: the provider stays the
 * source of truth and the actor for every mutation (it owns membership; inventing
 * our own store for that would be inventing a second identity system), and the
 * chrome is entirely ours — our search, our rows, our select, our button. Nothing
 * here re-implements an identity decision; it re-implements a list.
 *
 * Every action is a callback the host supplies, so this file imports no SDK and
 * the panel can be tested with four functions and no provider at all.
 */
/** What the expiry picker offers. Every entry is a real limit — there is no
 *  "whatever the provider does", because that is not an answer anyone can act on:
 *  it renders the same whether the underlying default is a day or forever. */
const EXPIRY_DAYS = [1, 3, 7, 14, 30];
/** The default. A month is long enough that a person on leave still gets in, and
 *  short enough that a forgotten invite stops being a way into the org. */
const EXPIRY_DEFAULT_DAYS = 30;
let VitesseMembersPanel = class VitesseMembersPanel extends LitElement {
    constructor() {
        super(...arguments);
        this.locale = new LocaleController(this);
        this.members = [];
        this.invitations = [];
        /** Role options for the per-row select — the same list the RBAC panel offers. */
        this.roleOptions = [];
        /** May this viewer change roles, invite, remove and revoke? */
        this.canManage = false;
        this.tab = 'members';
        this.filter = '';
        this.inviting = false;
        this.inviteEmail = '';
        this.inviteRole = '';
        /** How long the invite stays answerable, in days. An invite that outlives the
         *  reason for it is a standing way into the org that nobody is watching, so
         *  this always carries a real limit rather than deferring to a default nobody
         *  can see. */
        this.inviteDays = EXPIRY_DEFAULT_DAYS;
        this.busy = '';
        this.error = '';
    }
    /** LIGHT DOM — the shared stylesheet lives in the host document, so a shadow
     *  root would put every `.base-*` class out of reach. */
    createRenderRoot() {
        return this;
    }
    render() {
        void this.locale;
        return html `
      <div class="base-tab-list" role="tablist">
        ${this.tabButton('members', t('members.tabMembers'))}
        ${this.tabButton('invitations', t('members.tabInvitations'))}
      </div>
      ${this.error ? html `<p class="rbac-err">${this.error}</p>` : nothing}
      ${this.tab === 'members' ? this.membersTab() : this.invitationsTab()}
    `;
    }
    tabButton(id, label) {
        return html `<button
      class="base-tab"
      role="tab"
      aria-selected=${this.tab === id ? 'true' : 'false'}
      @click=${() => {
            this.tab = id;
            this.filter = '';
        }}
    >
      ${label}
    </button>`;
    }
    /** ONE search field for both tabs — it is the same act (narrow this list) and a
     *  second styling for it would be the drift this panel exists to remove. */
    search(placeholder) {
        return html `<label class="base-search-field">
      <input
        .value=${this.filter}
        placeholder=${placeholder}
        @input=${(e) => (this.filter = e.target.value)}
      />
      <vitesse-icon class="base-search-icon" name="search" size="14"></vitesse-icon>
    </label>`;
    }
    membersTab() {
        const q = this.filter.trim().toLowerCase();
        const shown = q
            ? this.members.filter((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
            : this.members;
        return html `
      <!-- The shared org-dialog hint slot (org-page-hint), so this description
           sits where every other org page puts its own. -->
      <p class="base-field-hint org-page-hint">${t('members.membersHint')}</p>
      <div class="members-toolbar">
        ${this.search(t('members.searchMembers'))}
        ${this.canManage && this.invite
            ? html `<button class="base-button base-button-blue base-button-sm" @click=${() => this.openInvite()}>
              <vitesse-icon name="user-plus" size="14"></vitesse-icon>${t('members.invite')}
            </button>`
            : nothing}
      </div>
      ${this.inviting ? this.inviteRow() : nothing}
      <p class="base-field-hint base-list-heading">${t('members.inThisOrg')}</p>
      ${shown.length
            ? html `<div class="base-roster-list base-roster-list--page">${shown.map((m) => this.memberRow(m))}</div>`
            : html `<p class="base-field-hint">${t('members.noMembers')}</p>`}
    `;
    }
    memberRow(m) {
        const busy = this.busy === m.userId;
        return html `<div class="base-roster-row">
      ${memberAvatar(m.name, m.imageUrl, () => this.requestUpdate())}
      <span class="rbac-who">
        <span class="rbac-name-text">${m.name}</span>
        ${m.email ? html `<span class="base-field-hint">${m.email}</span>` : nothing}
      </span>
      <span class="members-when">${m.joinedAtMs ? t('members.joined', { date: shortDate(m.joinedAtMs) }) : ''}</span>
      <span class="rbac-row-actions">
        ${this.canManage && this.setRole
            ? html `<vitesse-select
              size="md"
              .options=${this.roleOptions}
              .value=${m.role}
              ?disabled=${busy}
              @change=${(e) => void this.run(m.userId, () => this.setRole(m, e.detail.value))}
            ></vitesse-select>`
            : chip(m.role)}
        ${this.canManage && this.removeMember
            ? html `<button
              class="base-button base-button-red"
              ?disabled=${busy}
              title=${t('members.remove')}
              @click=${() => void this.run(m.userId, () => this.removeMember(m))}
            >
              <vitesse-icon name="trash" size="14"></vitesse-icon>${t('members.remove')}
            </button>`
            : nothing}
      </span>
    </div>`;
    }
    invitationsTab() {
        const q = this.filter.trim().toLowerCase();
        const shown = q ? this.invitations.filter((i) => i.email.toLowerCase().includes(q)) : this.invitations;
        return html `
      <p class="base-field-hint org-page-hint">${t('members.invitationsHint')}</p>
      <div class="members-toolbar">
        ${this.search(t('members.searchInvitations'))}
        ${this.canManage && this.invite
            ? html `<button class="base-button base-button-blue base-button-sm" @click=${() => this.openInvite()}>
              <vitesse-icon name="user-plus" size="14"></vitesse-icon>${t('members.invite')}
            </button>`
            : nothing}
      </div>
      ${this.inviting ? this.inviteRow() : nothing}
      <p class="base-field-hint base-list-heading">${t('members.awaitingReply')}</p>
      ${shown.length
            ? html `<div class="base-roster-list base-roster-list--page">
            ${shown.map((inv) => html `<div class="base-roster-row">
                <span class="rbac-avatar" aria-hidden="true"><vitesse-icon name="mail" size="14"></vitesse-icon></span>
                <span class="rbac-who">
                  <span class="rbac-name-text">${inv.email}</span>
                  <span class="base-field-hint">${t('members.invitedAs', { role: roleLabel(inv.role) })}</span>
                </span>
                <span class="members-when">${inv.createdAtMs ? t('members.invitedOn', { date: shortDate(inv.createdAtMs) }) : ''}</span>
                <span class="rbac-row-actions">
                  ${this.canManage && this.revokeInvitation
                ? html `<button
                        class="base-button base-button-red base-button-sm"
                        ?disabled=${this.busy === inv.id}
                        @click=${() => void this.run(inv.id, () => this.revokeInvitation(inv))}
                      >
                        <vitesse-icon name="x" size="14"></vitesse-icon>${t('members.revoke')}
                      </button>`
                : nothing}
                </span>
              </div>`)}
          </div>`
            : html `<div class="base-roster-list base-roster-list--page">
            <div class="base-roster-row base-roster-row--empty">
              <span class="rbac-avatar" aria-hidden="true"><vitesse-icon name="mail" size="14"></vitesse-icon></span>
              <span class="rbac-who"><span class="base-field-hint">${t('members.noInvitations')}</span></span>
            </div>
          </div>`}
    `;
    }
    openInvite() {
        this.inviting = true;
        this.inviteEmail = '';
        this.inviteRole = this.roleOptions[0]?.value ?? '';
        this.error = '';
    }
    /** The invite form is a ROW in the list it adds to, not a dialog over it: the
     *  thing being created is a row, and a modal for one text field costs a
     *  round-trip of attention for nothing. */
    inviteRow() {
        /*
         * NO AVATAR. Every other row on this list leads with a picture of the person
         * it is about; this row is about a person who does not exist yet, so a generic
         * silhouette in that slot is a placeholder pretending to be an identity. The
         * email field is the subject, and it starts where the names start.
         */
        return html `<div class="base-roster-row members-invite-row">
      <span class="rbac-who">
        <input
          class="rbac-name members-invite-email"
          type="email"
          .value=${this.inviteEmail}
          placeholder=${t('members.invitePlaceholder')}
          aria-label=${t('members.invitePlaceholder')}
          @input=${(e) => (this.inviteEmail = e.target.value)}
        />
      </span>
      <span class="rbac-row-actions">
        <vitesse-select
          size="md"
          .options=${this.roleOptions}
          .value=${this.inviteRole}
          @change=${(e) => (this.inviteRole = e.detail.value)}
        ></vitesse-select>
        <vitesse-select
          size="md"
          title=${t('members.expiresLabel')}
          .options=${EXPIRY_DAYS.map((d) => ({ value: String(d), label: t('members.expiresDays', { count: d }) }))}
          .value=${String(this.inviteDays)}
          @change=${(e) => (this.inviteDays = Number(e.detail.value) || EXPIRY_DEFAULT_DAYS)}
        ></vitesse-select>
        <button
          class="base-button base-button-blue members-invite-action"
          ?disabled=${!this.inviteEmail.trim() || this.busy === 'invite'}
          @click=${() => void this.sendInvite()}
        >
          ${this.busy === 'invite' ? t('members.sending') : t('members.send')}
        </button>
        <!-- Cancel reddens on hover only: it is the one control here that
             DISCARDS what was typed, and the platform spells "this undoes
             something" in red. Resting neutral, because it is not a danger until
             you are about to press it. -->
        <button class="base-button base-button-neutral members-invite-action members-invite-cancel" @click=${() => (this.inviting = false)}>
          ${t('members.cancel')}
        </button>
      </span>
    </div>`;
    }
    async sendInvite() {
        const email = this.inviteEmail.trim();
        if (!email || !this.invite)
            return;
        await this.run('invite', () => this.invite(email, this.inviteRole, this.inviteDays));
        if (!this.error)
            this.inviting = false;
    }
    /*
     * Every mutation goes through here so all four share one failure story: the row
     * is disabled while in flight, the reason is shown if it fails, and the panel
     * asks the host to re-read rather than patching its own copy. Optimism is wrong
     * for this screen — a membership change that renders as applied and was not is
     * the one mistake an access-control list must never make.
     */
    async run(key, op) {
        this.busy = key;
        this.error = '';
        try {
            await op();
            this.dispatchEvent(new CustomEvent('members-changed', { bubbles: true, composed: true }));
        }
        catch (e) {
            this.error = e instanceof Error ? e.message : String(e);
        }
        finally {
            this.busy = '';
        }
    }
};
__decorate([
    property({ attribute: false })
], VitesseMembersPanel.prototype, "members", void 0);
__decorate([
    property({ attribute: false })
], VitesseMembersPanel.prototype, "invitations", void 0);
__decorate([
    property({ attribute: false })
], VitesseMembersPanel.prototype, "roleOptions", void 0);
__decorate([
    property({ type: Boolean })
], VitesseMembersPanel.prototype, "canManage", void 0);
__decorate([
    property({ attribute: false })
], VitesseMembersPanel.prototype, "setRole", void 0);
__decorate([
    property({ attribute: false })
], VitesseMembersPanel.prototype, "removeMember", void 0);
__decorate([
    property({ attribute: false })
], VitesseMembersPanel.prototype, "invite", void 0);
__decorate([
    property({ attribute: false })
], VitesseMembersPanel.prototype, "revokeInvitation", void 0);
__decorate([
    state()
], VitesseMembersPanel.prototype, "tab", void 0);
__decorate([
    state()
], VitesseMembersPanel.prototype, "filter", void 0);
__decorate([
    state()
], VitesseMembersPanel.prototype, "inviting", void 0);
__decorate([
    state()
], VitesseMembersPanel.prototype, "inviteEmail", void 0);
__decorate([
    state()
], VitesseMembersPanel.prototype, "inviteRole", void 0);
__decorate([
    state()
], VitesseMembersPanel.prototype, "inviteDays", void 0);
__decorate([
    state()
], VitesseMembersPanel.prototype, "busy", void 0);
__decorate([
    state()
], VitesseMembersPanel.prototype, "error", void 0);
VitesseMembersPanel = __decorate([
    customElement('vitesse-members-panel')
], VitesseMembersPanel);
export { VitesseMembersPanel };
/** A date the way a roster reads it — the day, not the second. The exact instant
 *  belongs in the audit log; a member list only answers "roughly when".
 *
 *  Through `fmtDate` rather than `toLocaleDateString`, because the app's locale is
 *  the one the reader chose and the HOST's is whatever their OS happens to be —
 *  a guard test in this repo fails the build on the latter, and it is right to. */
function shortDate(ms) {
    return fmtDate(ms, { year: 'numeric', month: 'short', day: 'numeric' });
}
/** The provider spells its roles `org:admin`; a person reads "Admin". Stripping
 *  the prefix here rather than at the source keeps the raw value intact for the
 *  writes that need it. */
function roleLabel(role) {
    const bare = role.replace(/^org:/, '');
    return bare.charAt(0).toUpperCase() + bare.slice(1);
}
//# sourceMappingURL=vitesse-members-panel.js.map